import type { ChannelType } from "@workspace/api-client-react";

/**
 * What a timed (at_times) fire does when its channel is already playing:
 * `interrupt` plays now (cutting the current item), `wait` defers until the
 * current item finishes, `skip` drops this fire entirely.
 */
export type ConflictPolicy = "interrupt" | "wait" | "skip";

/** The concrete action the player takes for a fire vs. the on-air channel. */
export type ConflictResolution = "play" | "wait" | "skip";

/**
 * Per-schedule playback behavior carried by a resolved slot. The schedule
 * resolver populates these onto every slot and timed fire (`volume` 0–100,
 * `muteOthers` from the channel ducking rule below, `conflictPolicy` from the
 * entry's startBehavior); this module turns them into the concrete decisions
 * the player needs — effective output gain and which channels to duck — kept
 * pure so the DOM/audio side-effects in the player stay thin and testable.
 */
export interface SlotBehavior {
  /** Per-schedule output level, 0–100. Composes with the device global volume. */
  volume: number;
  /** When true, the other buses are ducked while this slot plays. */
  muteOthers: boolean;
  /** What a fire does if the channel is already playing (default interrupt). */
  conflictPolicy: ConflictPolicy;
}

/** A resolved slot's composed source kind (mirrors ResolvedSlot.source). */
export type SlotSource = "play" | "exception_play" | "off" | "live" | "none";

/**
 * The channel-level ducking rule — the single policy source shared by the
 * server resolver (which stamps `muteOthers` onto slots/fires) and the player
 * (which falls back to it when a timed fire's channel slot is a stale poll
 * snapshot): announcements always duck the other buses while they play
 * (CB-071 — announcement over video), and audio ad spots duck audible screen
 * content so a paid spot is never drowned out (CB-116). Music and video never
 * duck anyone.
 */
export function channelDucksOthers(channelType: ChannelType): boolean {
  return channelType === "announcements" || channelType === "ads";
}

/**
 * Whether a resolved slot requests `muteOthers`:
 *  - a `live` slot always does — a live broadcast takes its bus over and the
 *    other bus must go silent for its duration (channelType may be null: live
 *    events carry no entry, so the preview cannot attribute one);
 *  - otherwise the slot must be audibly on air (`play` / `exception_play` —
 *    never `off` or `none`) AND belong to a channel that ducks others.
 * This is the exact function the server applies at the resolver layer to every
 * slot on /schedule/preview, /schedule/resolve, /device/plan and /device/sync,
 * so preview == playback for ducking too.
 */
export function slotMuteOthers(
  channelType: ChannelType | null,
  source: SlotSource,
): boolean {
  if (source === "live") return true;
  if (source !== "play" && source !== "exception_play") return false;
  return channelType !== null && channelDucksOthers(channelType);
}

const DEFAULT_SLOT_VOLUME = 80;

function toConflictPolicy(value: unknown): ConflictPolicy {
  return value === "wait" || value === "skip" ? value : "interrupt";
}

/** Clamp an arbitrary number into the inclusive [0, 100] percentage range. */
function clampPercent(value: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

/**
 * Read the per-schedule behavior off a resolved slot or timed fire. The server
 * populates `volume`, `muteOthers` (via `slotMuteOthers`) and `conflictPolicy`
 * (the entry's startBehavior) on every resolved slot and timed fire; the reads
 * stay defensive so payloads cached before those fields shipped (offline
 * stores) degrade to the same defaults the resolver applies (`volume` 80,
 * `muteOthers` off, `conflictPolicy` interrupt).
 */
export function readSlotBehavior(slot: unknown): SlotBehavior {
  const s = slot as
    | { volume?: unknown; muteOthers?: unknown; conflictPolicy?: unknown }
    | null
    | undefined;
  const rawVolume = typeof s?.volume === "number" ? s.volume : Number(s?.volume);
  return {
    volume: clampPercent(rawVolume, DEFAULT_SLOT_VOLUME),
    muteOthers: s?.muteOthers === true,
    conflictPolicy: toConflictPolicy(s?.conflictPolicy),
  };
}

/**
 * Effective 0–1 output gain for a channel: the device global volume scaled by
 * the per-schedule slot volume, with mute collapsing it to silence. Both inputs
 * are 0–100 percentages; the result is clamped to [0, 1].
 *
 * `effectiveChannelGain(80, 50, false)` → 0.4 (80% × 50%).
 */
export function effectiveChannelGain(
  globalVol: number,
  slotVol: number,
  muted: boolean,
): number {
  if (muted) return 0;
  const g = clampPercent(globalVol, 0) / 100;
  const s = clampPercent(slotVol, 100) / 100;
  const out = g * s;
  if (!Number.isFinite(out)) return 0;
  return Math.max(0, Math.min(1, out));
}

/** An active audio slot considered when deciding ducking. */
export interface ActiveSlotBehavior {
  channelType: ChannelType;
  muteOthers: boolean;
}

/**
 * Given the slots currently on air, return the set of channels that should be
 * ducked (silenced) because some *other* active slot requested `muteOthers`.
 * A slot never ducks itself, so an announcement with `muteOthers=true` keeps
 * playing while music/ads under it go silent for its duration.
 */
export function duckedChannels(
  activeSlots: readonly ActiveSlotBehavior[],
): Set<ChannelType> {
  const ducked = new Set<ChannelType>();
  const dominators = activeSlots.filter((s) => s.muteOthers);
  if (dominators.length === 0) return ducked;
  for (const slot of activeSlots) {
    const duckedByOther = dominators.some(
      (d) => d.channelType !== slot.channelType,
    );
    if (duckedByOther) ducked.add(slot.channelType);
  }
  return ducked;
}

/**
 * Decide what a freshly-resolved timed fire does given its conflict policy and
 * whether the fire's channel is already playing:
 *   • `interrupt` → always `play` (cut the current item now).
 *   • `wait`      → `wait` if the channel is busy (let the current item finish,
 *                   then play), else `play`.
 *   • `skip`      → `skip` if the channel is busy (drop this fire), else `play`.
 * When nothing is playing on the channel, every policy resolves to `play`.
 */
export function resolveConflict(
  policy: ConflictPolicy,
  channelIsPlaying: boolean,
): ConflictResolution {
  if (!channelIsPlaying) return "play";
  if (policy === "wait") return "wait";
  if (policy === "skip") return "skip";
  return "play";
}
